import { getEffectiveSeatType, getHighestRole, USER_SEAT_LABELS } from '@paintscout/util'
import { formatName, getCustomPermissionLabels } from '@paintscout/util/builder'
import { useIsMobile } from '@paintscout/ui'
import { useClientOptions, useUser, useUserPermissions, useUsers } from '@paintscout/ui-business'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { isE2E } from 'shared/util/e2e'
import { useEffect, useRef } from 'react'
// Use the raw Sentry SDK rather than an app-specific `appSentry` module so this component
// can be shared across apps (estimator + painter-view). `captureException` is a no-op if
// the host app never called `Sentry.init`.
import * as Sentry from '@sentry/react'
import userflow from 'userflow.js'

import moment from 'moment'

export default function UserFlow() {
  const { user, isSuperadmin, nonBilling, isTrial, trialExpiry, preferences } = useUser()
  const { users } = useUsers()
  const { permissionToUpdateBilling } = useUserPermissions()
  const { clientId, options } = useClientOptions()
  const mobile = useIsMobile()
  const screenWidth = window?.screen?.width || 0
  // betaFeaturesSelfServe gates the Settings > "Beta Features" section, so it marks who has beta access (ENG-2041).
  const { crm: crmFlag, sherwinWilliams, betaFeaturesSelfServe } = useFlags()
  const seatType = getEffectiveSeatType({ seatType: user.app_metadata?.seatType, roles: user.app_metadata.roles })
  const userTypeLabel = seatType ? USER_SEAT_LABELS[seatType] : undefined
  const crmTrialExpiration = options?.options?.crm?.trialExpiration
  const isOnCrmTrial = crmTrialExpiration && crmFlag ? moment().isBefore(moment(crmTrialExpiration)) : false
  const leadSource = user?.app_metadata?.company?.leadSource
  const owner = user?.app_metadata?.isOwner
  const companyCreated = user?.app_metadata?.company?.created

  const hasCreatedQuote = options?.options?.onboardingTasks?.hasCreatedQuote || false
  const hasCreatedDeal = options?.options?.onboardingTasks?.hasCreatedDeal || false
  const hasCreatedEvent = options?.options?.onboardingTasks?.hasCreatedEvent || false
  const hasCreatedProductionEvent = options?.options?.onboardingTasks?.hasCreatedProductionEvent || false

  const productOptions = Object.values(options?.options?.products?.values || {})
  const hasSherwinWilliamsProduct = productOptions.some((product) => product.manufacturer === 'Sherwin-Williams')

  const hasActiveCrewUsers = users.some(
    (u) => getHighestRole(u.app_metadata?.roles) === 'crew' && u.app_metadata.active
  )

  const getLifecycleStage = () => {
    if (nonBilling) {
      return 'Non Billing'
    }
    if (isTrial) {
      return 'Trial'
    }
    if (user?.app_metadata?.company?.active) {
      return 'Customer'
    }
    if (!user?.app_metadata?.company?.active) {
      return 'Past Customer'
    }
  }

  // Flags arrive after first render (LDProvider is not async), so re-identify when a flag the payload
  // reads changes value.
  const lastFlagsKeyRef = useRef<string | null>(null)
  const userflowInitRef = useRef(false)

  useEffect(() => {
    // Skip during automated BugBug runs (ps-e2e, set by the shared login component) so the
    // "what's new" resource-center popup can't float above a button a test is trying to click.
    // Mirrors the isE2E() guard PostHogIdentify already uses to skip session replay.
    if (isSuperadmin || !process.env.USER_FLOW_TOKEN || isE2E()) {
      return
    }

    const run = async () => {
      const lifecycleStage = getLifecycleStage()

      // Prepare payloads
      const identityPayload = JSON.stringify({
        name: formatName(user.user_metadata),
        'First Name': user.user_metadata.firstName,
        'Last Name': user.user_metadata.lastName,
        email: user.email,
        'User Type': userTypeLabel,
        Active: user.app_metadata.active ? 'Yes' : 'No',
        'Device Type': mobile ? 'Mobile' : 'Desktop',
        'New Look': 'Yes',
        'CRM Enabled': crmFlag ? 'Yes' : 'No',
        'Production Enabled': crmFlag ? 'Yes' : 'No',
        'PaintScout User ID': user.user_id,
        'Side Nav Showing': screenWidth > 1213 && seatType !== 'painter' ? 'Yes' : 'No',
        'Company ID': clientId,
        'CRM Trial': isOnCrmTrial ? 'Yes' : 'No',
        'SW Product Browser Enabled': sherwinWilliams ? 'Yes' : 'No',
        'Beta Features Access': betaFeaturesSelfServe ? 'Yes' : 'No',
        'Logo Uploaded': options?.options.logo?.value ? 'Yes' : 'No',
        'Lead Source': leadSource,
        'Integrations Enabled': Object.keys(preferences?.integrations || {}),
        'Permission to Update Billing': permissionToUpdateBilling ? 'Yes' : 'No',
        'Custom Permissions': getCustomPermissionLabels(user?.app_metadata?.customPermissions),
        Owner: owner ? 'Yes' : 'No'
      })
      const groupPayload = JSON.stringify({
        name: options?.options.companyName.value,
        Active: user.app_metadata.company?.active ? 'Yes' : 'No',
        'Billing ID': user.app_metadata?.company?.billingId,
        'Lifecycle Stage': lifecycleStage,
        'Parent Company ID Present': user.app_metadata?.company?.parentCompanyId ? 'Yes' : 'No',
        'Parent Company ID': user.app_metadata?.company?.parentCompanyId,
        'PaintScout Company ID': clientId,
        'Lead Source': leadSource,
        // Userflow infers a datetime attribute only from an ISO 8601 string, not from epoch ms.
        'Trial Expiry Date': trialExpiry ? new Date(trialExpiry).toISOString() : undefined,
        'Company Created Date': companyCreated ? new Date(companyCreated).toISOString() : undefined,
        'Created Quote': hasCreatedQuote ? 'Yes' : 'No',
        'Created Deal': hasCreatedDeal ? 'Yes' : 'No',
        'Created Event': hasCreatedEvent ? 'Yes' : 'No',
        'Created Job': hasCreatedProductionEvent ? 'Yes' : 'No',
        'Has SW Product': hasSherwinWilliamsProduct ? 'Yes' : 'No',
        'Integrations Enabled': Object.keys(options?.options?.integrations || {}),
        'Has Active Crew Users': hasActiveCrewUsers ? 'Yes' : 'No',
        '# Active Users':
          seatType === 'painter' ? undefined : users.filter((u) => u.app_metadata.active !== false).length
      })

      if (!userflowInitRef.current) {
        userflowInitRef.current = true
        userflow.setInferenceClassNameFilter((cls) => !cls.startsWith('Mui') && !cls.startsWith('jss'))
        userflow.setInferenceAttributeFilter('id', (_id) => false)
        userflow.setInferenceAttributeNames(['data-tour-id'])
        userflow.setUrlFilter((url) => {
          const parsed = new URL(url)
          const filtered = new URLSearchParams()
          if (parsed.searchParams.has('section')) {
            filtered.set('section', parsed.searchParams.get('section'))
          }
          parsed.search = filtered.toString()
          return parsed.toString()
        })
        userflow.setBaseZIndex(2147483001)
        userflow.init(process.env.USER_FLOW_TOKEN)
        userflow.setResourceCenterLauncherHidden(seatType === 'painter')
      }

      const flagsKey = `${crmFlag}|${betaFeaturesSelfServe}`
      if (lastFlagsKeyRef.current !== flagsKey) {
        // Identity-verification signatures are computed server-side in the Auth0 post-login action
        // and read from app_metadata so USER_FLOW_SECRET_KEY never ships in the client bundle
        // (#19036). Fall back to unsigned calls when a signature is absent (e.g. the Auth0 secret
        // isn't configured yet) to preserve prior behavior.
        const userIdSig = user.app_metadata?.userFlowUserIdSignature
        const companyIdSig = user.app_metadata?.userFlowCompanyIdSignature

        userflow.identify(user.user_id, JSON.parse(identityPayload), userIdSig ? { signature: userIdSig } : undefined)

        userflow.group(clientId, JSON.parse(groupPayload), companyIdSig ? { signature: companyIdSig } : undefined)
      }
      lastFlagsKeyRef.current = flagsKey
    }

    run().catch((error) => {
      console.log('Userflow error:', error)
      Sentry.captureException(error)
    })
  }, [user?.user_id, clientId, crmFlag, betaFeaturesSelfServe])

  return null
}
